import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ModalService } from '@rocketfinancialcorp/rocket-ui/modal';

import { NoteItemComponent, NotesModalComponent } from '@shared/components';
import { Note, NoteCreateProps } from '@shared/models';
import { AccessControlPipe } from '@shared/pipes';
import { NoteService } from '@shared/services';
import { ErrorUtils } from '@shared/utils';

@Component({
  selector: 'app-details-notes',
  templateUrl: './details-notes.component.html',
  styleUrls: ['./details-notes.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RktButtonDirective, NoteItemComponent, AsyncPipe, AccessControlPipe, RktIconComponent],
})
export class DetailsNotesComponent implements OnInit, OnDestroy {
  @ViewChild('latestNoteItem') noteItem!: NoteItemComponent;

  @Input() entityType!: NoteCreateProps['entityType'];

  @Input() entityId!: NoteCreateProps['entityId'];

  @Input() isCompact = false;

  @Output() hasNotesStateChanged = new EventEmitter<boolean>();

  latestNote?: Note;

  loading = true;

  get isShowPlaceholder() {
    return this.loading && !this.latestNote;
  }

  private destroy$ = new Subject<void>();

  constructor(
    private noteService: NoteService,
    private modalService: ModalService,
  ) {}

  ngOnInit(): void {
    this.getLatestNote();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getLatestNote(): void {
    this.loading = true;
    this.noteService
      .getNotes({ entityType: this.entityType, entityId: this.entityId, page: 0, size: 1 })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (notes: Note[]) => {
          this.latestNote = notes[0];
          this.hasNotesStateChanged.emit(!!notes.length);
        },
        error: (error) => {
          ErrorUtils.catchError('noteService.getNotes', error);
        },
      });
  }

  onViewAllBtnClick(): void {
    this.noteItem?.onNoteEditCancel();
    const notesModalRef = this.modalService.open(NotesModalComponent, { size: 'half-size', isDraggable: false, className: 'notes-list' });

    notesModalRef.componentInstance.entityType = this.entityType;
    notesModalRef.componentInstance.entityId = this.entityId;

    notesModalRef.result.then(
      () => this.getLatestNote(),
      () => this.getLatestNote(),
    );
  }
}
